import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import FormLayout from '../../../components/Common/FormLayout';

interface ProductivityFormData {
  veiculo_id: number | '';
  motorista_id: number | '';
  veiculo_placa: string;
  motorista_nome: string;
  periodo: string;
  data_referencia: string;
  quilometragem_percorrida: number | '';
  horas_trabalhadas: number | '';
  viagens_realizadas: number | '';
  consumo_medio: number | '';
  litros_consumidos: number | '';
  custo_total: number | '';
  custo_combustivel: number | '';
  custo_manutencao: number | '';
  custo_pedagio: number | '';
  custo_outros: number | '';
  eficiencia: number | '';
  status: string;
  observacoes: string;
}

const ProductivityCreate: React.FC = () => {
  const navigate = useNavigate();
  const [veiculos, setVeiculos] = useState<any[]>([]);
  const [motoristas, setMotoristas] = useState<any[]>([]);
  const [formData, setFormData] = useState<ProductivityFormData>({
    veiculo_id: '',
    motorista_id: '',
    veiculo_placa: '',
    motorista_nome: '',
    periodo: '',
    data_referencia: '',
    quilometragem_percorrida: '',
    horas_trabalhadas: '',
    viagens_realizadas: '',
    consumo_medio: '',
    litros_consumidos: '',
    custo_total: '',
    custo_combustivel: '',
    custo_manutencao: '',
    custo_pedagio: '',
    custo_outros: '',
    eficiencia: '',
    status: 'Regular',
    observacoes: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  useEffect(() => {
    loadVehiclesAndDrivers();
  }, []);

  const loadVehiclesAndDrivers = async () => {
    try {
      const [vehiclesResp, driversResp] = await Promise.all([
        apiService.getVehicles({ page: 1, limit: 100 }),
        apiService.getDrivers({ page: 1, limit: 100 })
      ]);

      if (vehiclesResp.success && vehiclesResp.data) {
        setVeiculos(vehiclesResp.data || []);
      }

      if (driversResp.success && driversResp.data) {
        setMotoristas(driversResp.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar veículos e motoristas:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Handle vehicle selection
    if (name === 'veiculo_id' && value) {
      const selectedVehicle = veiculos.find(v => v.id === parseInt(value));
      setFormData(prev => ({
        ...prev,
        veiculo_id: parseInt(value),
        veiculo_placa: selectedVehicle?.placa || ''
      }));
    }
    // Handle driver selection
    else if (name === 'motorista_id' && value) {
      const selectedDriver = motoristas.find(m => m.id === parseInt(value));
      setFormData(prev => ({
        ...prev,
        motorista_id: parseInt(value),
        motorista_nome: selectedDriver?.nome || ''
      }));
    }
    else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.periodo.trim()) {
      newErrors.periodo = 'Período é obrigatório';
    }

    if (!formData.data_referencia) {
      newErrors.data_referencia = 'Data de referência é obrigatória';
    }

    if (formData.quilometragem_percorrida === '' || Number(formData.quilometragem_percorrida) < 0) {
      newErrors.quilometragem_percorrida = 'Quilometragem percorrida é obrigatória e não pode ser negativa';
    }

    if (formData.horas_trabalhadas === '' || Number(formData.horas_trabalhadas) < 0) {
      newErrors.horas_trabalhadas = 'Horas trabalhadas é obrigatória e não pode ser negativa';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      utils.showNotification('Por favor, corrija os erros no formulário', { type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      const submitData = {
        veiculo_id: formData.veiculo_id || undefined,
        motorista_id: formData.motorista_id || undefined,
        veiculo_placa: formData.veiculo_placa || undefined,
        motorista_nome: formData.motorista_nome || undefined,
        periodo: formData.periodo,
        data_referencia: formData.data_referencia,
        quilometragem_percorrida: Number(formData.quilometragem_percorrida),
        horas_trabalhadas: Number(formData.horas_trabalhadas),
        viagens_realizadas: formData.viagens_realizadas === '' ? undefined : Number(formData.viagens_realizadas),
        consumo_medio: formData.consumo_medio === '' ? undefined : Number(formData.consumo_medio),
        litros_consumidos: formData.litros_consumidos === '' ? undefined : Number(formData.litros_consumidos),
        custo_total: formData.custo_total === '' ? undefined : Number(formData.custo_total),
        custo_combustivel: formData.custo_combustivel === '' ? undefined : Number(formData.custo_combustivel),
        custo_manutencao: formData.custo_manutencao === '' ? undefined : Number(formData.custo_manutencao),
        custo_pedagio: formData.custo_pedagio === '' ? undefined : Number(formData.custo_pedagio),
        custo_outros: formData.custo_outros === '' ? undefined : Number(formData.custo_outros),
        eficiencia: formData.eficiencia === '' ? undefined : Number(formData.eficiencia),
        status: formData.status,
        observacoes: formData.observacoes || undefined
      };

      const response = await apiService.createProductivityMetric(submitData);

      if (response.success) {
        utils.showNotification('Métrica de produtividade cadastrada com sucesso!', { type: 'success' });
        navigate('/produtividade');
      } else {
        throw new Error(response.message || 'Erro ao cadastrar métrica de produtividade');
      }
    } catch (error: any) {
      console.error('Erro ao cadastrar métrica de produtividade:', error);
      utils.showNotification(error.message || 'Erro ao cadastrar métrica de produtividade', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    const fakeEvent = {
      preventDefault: () => {}
    } as React.FormEvent;
    handleSubmit(fakeEvent);
  };

  const handleCancel = () => {
    navigate('/produtividade');
  };

  return (
    <FormLayout
      title="Nova Métrica de Produtividade"
      subtitle="Cadastro de Métrica de Produtividade"
      onCancel={handleCancel}
      onSave={handleSave}
      saveButtonText="Cadastrar Métrica"
      isLoading={isLoading}
      showActions={false}
    >
      <form onSubmit={handleSubmit}>
        {/* Informações Básicas */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Informações Básicas
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Veículo</label>
              <select
                name="veiculo_id"
                value={formData.veiculo_id}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="">Selecione um veículo (opcional)</option>
                {veiculos.map(veiculo => (
                  <option key={veiculo.id} value={veiculo.id}>
                    {veiculo.placa} - {veiculo.marca} {veiculo.modelo}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Placa do Veículo (texto)</label>
              <input
                type="text"
                name="veiculo_placa"
                value={formData.veiculo_placa}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Ex: ABC-1234"
                maxLength={20}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Motorista</label>
              <select
                name="motorista_id"
                value={formData.motorista_id}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="">Selecione um motorista (opcional)</option>
                {motoristas.map(motorista => (
                  <option key={motorista.id} value={motorista.id}>
                    {motorista.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Nome do Motorista (texto)</label>
              <input
                type="text"
                name="motorista_nome"
                value={formData.motorista_nome}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Ex: João Silva"
                maxLength={100}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Período *</label>
              <input
                type="text"
                name="periodo"
                value={formData.periodo}
                onChange={handleInputChange}
                className={`form-control ${errors.periodo ? 'error' : ''}`}
                placeholder="Ex: Janeiro/2024"
                maxLength={50}
              />
              {errors.periodo && <span className="error-message">{errors.periodo}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Data de Referência *</label>
              <input
                type="date"
                name="data_referencia"
                value={formData.data_referencia}
                onChange={handleInputChange}
                className={`form-control ${errors.data_referencia ? 'error' : ''}`}
              />
              {errors.data_referencia && <span className="error-message">{errors.data_referencia}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="Excelente">Excelente</option>
                <option value="Bom">Bom</option>
                <option value="Regular">Regular</option>
                <option value="Ruim">Ruim</option>
              </select>
            </div>
          </div>
        </div>

        {/* Métricas Operacionais */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Métricas Operacionais
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Quilometragem Percorrida (km) *</label>
              <input
                type="number"
                name="quilometragem_percorrida"
                value={formData.quilometragem_percorrida}
                onChange={handleInputChange}
                className={`form-control ${errors.quilometragem_percorrida ? 'error' : ''}`}
                placeholder="0"
                min="0"
                step="0.01"
              />
              {errors.quilometragem_percorrida && <span className="error-message">{errors.quilometragem_percorrida}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Horas Trabalhadas *</label>
              <input
                type="number"
                name="horas_trabalhadas"
                value={formData.horas_trabalhadas}
                onChange={handleInputChange}
                className={`form-control ${errors.horas_trabalhadas ? 'error' : ''}`}
                placeholder="0"
                min="0"
                step="0.01"
              />
              {errors.horas_trabalhadas && <span className="error-message">{errors.horas_trabalhadas}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Viagens Realizadas</label>
              <input
                type="number"
                name="viagens_realizadas"
                value={formData.viagens_realizadas}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0"
                min="0"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Consumo Médio (km/L)</label>
              <input
                type="number"
                name="consumo_medio"
                value={formData.consumo_medio}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.0"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Litros Consumidos</label>
              <input
                type="number"
                name="litros_consumidos"
                value={formData.litros_consumidos}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.0"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Eficiência (%)</label>
              <input
                type="number"
                name="eficiencia"
                value={formData.eficiencia}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0"
                min="0"
                max="100"
                step="0.01"
              />
            </div>
          </div>
        </div>

        {/* Custos */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Custos
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Custo Total (R$)</label>
              <input
                type="number"
                name="custo_total"
                value={formData.custo_total}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Custo Combustível (R$)</label>
              <input
                type="number"
                name="custo_combustivel"
                value={formData.custo_combustivel}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Custo Manutenção (R$)</label>
              <input
                type="number"
                name="custo_manutencao"
                value={formData.custo_manutencao}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Custo Pedágio (R$)</label>
              <input
                type="number"
                name="custo_pedagio"
                value={formData.custo_pedagio}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Outros Custos (R$)</label>
              <input
                type="number"
                name="custo_outros"
                value={formData.custo_outros}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </div>

        {/* Observações */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Observações
          </h3>

          <div className="form-group">
            <label className="form-label">Observações Gerais</label>
            <textarea
              name="observacoes"
              value={formData.observacoes}
              onChange={handleInputChange}
              className="form-control"
              rows={4}
              placeholder="Informações adicionais sobre a métrica de produtividade..."
            />
          </div>
        </div>

        {/* Botões de Ação */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={isLoading}
          >
            <i className="fas fa-times"></i>
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Cadastrando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Cadastrar Métrica
              </>
            )}
          </button>
        </div>
      </form>
    </FormLayout>
  );
};

export default ProductivityCreate;
